import { fetchFile } from '@ffmpeg/util';

import { getFFmpegInstance } from '@/components/VideoClipper/utils/ffmpeg';

import { FileSize, Resolution } from '../type';

// 所有视频类型
export const VideoType = {
  mp4: ['.mp4', 'video/mp4'],
  webm: ['.webm', 'video/webm'],
  mov: ['.mov', 'video/quicktime'],
};

export const isVideo = (type: string | undefined) => {
  if (!type) return false;
  return Object.values(VideoType).flat().includes(type);
};

export const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const lowercaseUrl = url.toLowerCase();
  const videoExtensions = Object.values(VideoType)
    .flat()
    .filter((type) => type.startsWith('.'))
    .map((ext) => ext.toLowerCase());

  return videoExtensions.some((ext) => lowercaseUrl.endsWith(ext));
};

// 所有音频类型
export const AudioType = {
  mp3: ['.mp3', 'audio/mpeg'],
  wav: ['.wav', 'audio/wav'],
  m4a: ['.m4a', 'audio/mp4', 'audio/x-m4a'],
};

export const isAudio = (type: string | undefined) => {
  if (!type) return false;
  return Object.values(AudioType).flat().includes(type);
};

export const ImageType = {
  jpg: ['.jpg', '.jpeg', 'image/jpeg'],
  png: ['.png', 'image/png'],
  webp: ['.webp', 'image/webp'],
};

export const isImage = (type: string | undefined) => {
  if (!type) return false;
  return Object.values(ImageType).flat().includes(type);
};

export const AllFileType = {
  ...VideoType,
  ...AudioType,
  ...ImageType,
};

export const getUrlSuffix = (url: string) => {
  const suffix = url.split('.').at(-1);
  if (!suffix) return '';

  return `.${suffix}`;
};

const handleCreateVideo = (url: string | File): HTMLVideoElement => {
  const videoURL = typeof url === 'string' ? url : URL.createObjectURL(url);
  const video = document.createElement('video');
  video.src = videoURL;
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.autoplay = true;

  return video;
};

// 清理视频
const handleVideoCleanUp = (video: HTMLVideoElement) => {
  URL.revokeObjectURL(video.src);
  video.remove();
};

// base64转file
const base64ToFile = (base64: string, filename: string): File | null => {
  try {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Failed to convert base64 img to file:', error);
    return null;
  }
};

// 从base64获取视频封面
const getVideoCoverFromBase64 = (
  canvas: HTMLCanvasElement,
): { coverUrl: string; coverFile: File | null } => {
  try {
    const coverUrl = canvas.toDataURL('image/jpeg');
    const coverFile = base64ToFile(coverUrl, 'cover.jpeg');

    if (coverUrl && coverFile) {
      return { coverUrl, coverFile };
    } else {
      return { coverUrl: '', coverFile: null };
    }
  } catch (error) {
    console.error('Failed to convert canvas to base64:', error);
    return { coverUrl: '', coverFile: null };
  }
};

// 获取视频封面
const handleGetVideoCover = (
  video: HTMLVideoElement,
): Promise<{ coverUrl: string; coverFile: File | null }> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    const useDefaultCover = () => {
      console.error('Failed to get video cover, use default cover instead');
      resolve({
        coverUrl:
          'http://rc.vcmresource.com/web/video-marketing/prod/_next/static/images/default-upload-cover.png',
        coverFile: null,
      });

      canvas.remove();
    };

    if (!ctx) {
      return useDefaultCover();
    }

    try {
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const coverFile = new File([blob], 'image.jpeg', { type: 'image/jpeg' });
            resolve({
              coverUrl: URL.createObjectURL(blob),
              coverFile,
            });
            canvas.remove();
          }
        },
        'image/jpeg',
        0.5,
      );
    } catch (error) {
      useDefaultCover();
    }
  });
};

// 处理视频错误
const handleVideoError = (video: HTMLVideoElement) => {
  return new Promise((resolve, reject) => {
    const mediaError = video.error;
    let errorDetails = '';

    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorDetails = '播放被用户中止';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorDetails = '网络错误导致视频下载失败';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorDetails = '视频解码失败，可能是格式或编码器不兼容';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorDetails = '视频格式或MIME类型不受支持';
          // 检查视频格式支持情况
          const videoElement = document.createElement('video');
          const formats = {
            mp4: 'video/mp4',
            webm: 'video/webm',
            ogg: 'video/ogg',
          };
          const supportedFormats = Object.entries(formats)
            .filter(([_, type]) => videoElement.canPlayType(type))
            .map(([format]) => format)
            .join(', ');
          errorDetails += `\n支持的格式: ${supportedFormats}`;
          break;
        default:
          errorDetails = '未知错误';
      }
    }

    console.error('Failed to load video:', {
      url: video.src,
      errorCode: mediaError?.code,
      errorMessage: mediaError?.message,
      details: errorDetails,
    });

    reject(`Failed to load video: ${mediaError?.message}`);
  });
};

// 获取音频信息
export const getAudioInfo = (file: string | File): Promise<{ duration: number }> => {
  const audio = document.createElement('audio');
  audio.src = typeof file === 'string' ? file : URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    audio.addEventListener('loadedmetadata', () => {
      resolve({
        duration: audio.duration,
      });
      audio.remove();
    });

    audio.addEventListener('error', async (e) => {
      if (typeof file === 'string') {
        audio.remove();
        reject('fail to load audio');
      } else {
        const ext = file.name.split('.').pop() || 'mp4';
        const inputFileName = `${Date.now()}_input.${ext}`;
        let logStack = '';

        const logHandler = ({ message }: { message: string }) => {
          logStack += message + '\n';
        };

        const ffmpeg = await getFFmpegInstance();
        ffmpeg.on('log', logHandler);

        try {
          await ffmpeg.writeFile(inputFileName, await fetchFile(file));
          // 只探测，不转码
          await ffmpeg.exec(['-i', inputFileName]);
          // 日志里找 Duration 字段
          const match = logStack.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);
          if (match) {
            const [, h, m, s] = match;
            resolve({
              duration: parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s),
            });
            return;
          }
          reject('fail to load audio');
          throw new Error('未能解析到时长');
        } catch (err) {
          throw err;
        } finally {
          ffmpeg.off('log', logHandler);
          await ffmpeg.deleteFile(inputFileName);
          logStack = '';

          audio.remove();
        }
      }
    });
  });
};

// 获取视频信息
export const getVideoInfo = (
  data: string | File,
): Promise<{ width: number; height: number; duration: number }> => {
  const video = handleCreateVideo(data);

  return new Promise((resolve, reject) => {
    video.addEventListener('loadedmetadata', () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
      handleVideoCleanUp(video);
    });

    video.addEventListener('error', (e) => {
      handleVideoError(video).catch((e) => {
        reject(e);
        handleVideoCleanUp(video);
      });
    });
  });
};

export const getImageInfo = (
  data: string | File,
): Promise<{ width: number; height: number; coverUrl: string }> => {
  return new Promise((resolve, reject) => {
    if (typeof data === 'string') {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // 处理跨域
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          coverUrl: data,
        });
      };
      img.onerror = (e) => {
        reject(e);
      };
      img.src = data;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
            coverUrl: e.target?.result as string,
          });
        };
        img.onerror = (e) => {
          reject(e);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsDataURL(data);
    }
  });
};

// 获取视频信息和第一帧封面
export const getVideoInfoAndFirstFrame = (
  data: string | File,
): Promise<{
  width: number;
  height: number;
  duration: number;
  coverUrl: string;
  coverFile: File | null;
}> => {
  const video = handleCreateVideo(data);

  return new Promise((resolve, reject) => {
    video.addEventListener('loadeddata', () => {
      video.currentTime = 0;
      setTimeout(async () => {
        try {
          await video.play();
          video.pause();

          const { coverUrl, coverFile } = await handleGetVideoCover(video);

          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration,
            coverUrl: coverUrl,
            coverFile: coverFile,
          });

          handleVideoCleanUp(video);
        } catch (error) {
          console.error(`Failed to play video: ${error}`);
          reject(error);
          handleVideoCleanUp(video);
        }
      }, 100);
    });

    video.addEventListener('error', (e) => {
      handleVideoError(video).catch((e) => {
        reject(e);
        handleVideoCleanUp(video);
      });
    });
  });
};

// 获取视频信息和第一帧封面
export const getVideoInfoAndFrame = (
  data: string | File,
): Promise<{
  width: number;
  height: number;
  duration: number;
  coverUrl: string;
  coverFile: File | null;
}> => {
  const video = handleCreateVideo(data);

  return new Promise((resolve, reject) => {
    video.addEventListener('loadeddata', () => {
      const duration = video.duration;
      let capture = 0;

      if (duration >= 5) {
        capture = 5;
      } else {
        capture = +(duration / 3).toFixed(1);
      }
      console.log(capture, '---capture');
      video.currentTime = capture;
      setTimeout(async () => {
        try {
          await video.play();
          video.pause();

          const { coverUrl, coverFile } = await handleGetVideoCover(video);

          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration,
            coverUrl: coverUrl,
            coverFile: coverFile,
          });

          handleVideoCleanUp(video);
        } catch (error) {
          console.error(`Failed to play video: ${error}`);
          reject(error);
          handleVideoCleanUp(video);
        }
      }, 100);
    });

    video.addEventListener('error', (e) => {
      handleVideoError(video).catch((e) => {
        reject(e);
        handleVideoCleanUp(video);
      });
    });
  });
};

// 判断视频是否需要转码
export const determineTranscode = (file: File | string): Promise<boolean> => {
  const video = handleCreateVideo(file);

  return new Promise((resolve, reject) => {
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0;

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      setTimeout(async () => {
        try {
          await video.play();
          video.pause();

          if (!ctx) {
            resolve(true);
          } else {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
              (blob) => (blob ? resolve(false) : resolve(true)),
              'image/jpeg',
              0.5,
            );
          }
        } catch (error) {
          resolve(true);
        } finally {
          canvas.remove();
          handleVideoCleanUp(video);
        }
      }, 100);
    });

    video.addEventListener('error', (e) => {
      resolve(true);
      handleVideoCleanUp(video);
    });
  });
};

export const resolutionValidate = (
  origin_resolution: `${number}*${number}`,
  validate_resolution: Resolution,
) => {
  const [origin_width, origin_height] = origin_resolution.split('*');

  let rule_width = 0;
  let rule_height = 0;

  if (validate_resolution?.includes('*')) {
    const [_width, _height] = validate_resolution.split('*');
    rule_width = Number(_width);
    rule_height = Number(_height);
  } else {
    switch (validate_resolution) {
      case '480p':
        rule_width = 854;
        rule_height = 480;
        break;
      case '720p':
        rule_width = 1280;
        rule_height = 720;
        break;
      case '1080p':
        rule_width = 1920;
        rule_height = 1080;
        break;
      case '2k':
        rule_width = 2560;
        rule_height = 1440;
        break;
      case '4k':
        rule_width = 4096;
        rule_height = 2160;
        break;
      default:
        break;
    }
  }

  const isHorizontal =
    Number(origin_width) <= Number(rule_width) &&
    Number(origin_height) <= Number(rule_height);

  const isVertical =
    Number(origin_width) <= Number(rule_height) &&
    Number(origin_height) <= Number(rule_width);

  return isHorizontal || isVertical;
};

const resolutionMap: Record<string, { long: number; short: number }> = {
  '480p': { long: 854, short: 480 },
  '720p': { long: 1280, short: 720 },
  '1080p': { long: 1920, short: 1080 },
  '2k': { long: 2560, short: 1440 },
  '4k': { long: 3840, short: 2160 },
  '8k': { long: 7680, short: 4320 },
};
export const imageResolutionSingleValidate = (
  origin_resolution: string,
  validate_resolution: keyof typeof resolutionMap,
) => {
  const [w, h] = origin_resolution.split('*').map(Number);
  const { long, short } = resolutionMap[validate_resolution];

  const maxSide = Math.max(w, h);
  const minSide = Math.min(w, h);

  return maxSide <= long && minSide <= short;
};

export const fileSizeValidate = (origin_size: number, validate_size: FileSize) => {
  let maxSizeInBytes;

  if (typeof validate_size === 'string') {
    const unit = validate_size.slice(-2).toUpperCase();
    const sizeValue = parseFloat(validate_size.slice(0, -2));

    switch (unit) {
      case 'KB':
        maxSizeInBytes = sizeValue * 1024;
        break;
      case 'MB':
        maxSizeInBytes = sizeValue * 1024 * 1024;
        break;
      case 'GB':
        maxSizeInBytes = sizeValue * 1024 * 1024 * 1024;
        break;
      default:
        maxSizeInBytes = sizeValue * 1024 * 1024;
        break;
    }
  } else {
    // 默认为 MB
    maxSizeInBytes = validate_size * 1024 * 1024;
  }

  return origin_size <= maxSizeInBytes;
};
